import type { NodeStatus } from '../api/types';
import { formatGb, percent } from '../format';

type NodeVm = NonNullable<NodeStatus['vms']>[number];

const KIND_LABEL: Record<NodeVm['kind'], string> = {
  server: 'サーバー',
  test: 'Mod テスト',
  infra: '基盤',
  personal: '個人用',
  other: 'その他',
};

/**
 * ノードの上の VM を、Panel が管理していないものも含めて見せる（閲覧だけ。操作は付けない）。
 * 「何も動かしていないのにメモリや CPU が使われている」ときに、何が使っているかを確かめるため。
 * メインの表示ではないので、閉じた状態の簡単なリストにする
 */
export function NodeVms({ node }: { node: NodeStatus & { vms: NodeVm[] } }) {
  const vms = node.vms.toSorted((a, b) => Number(b.running) - Number(a.running) || b.memoryUsedMb - a.memoryUsedMb);
  const running = vms.filter((vm) => vm.running);
  const vmUsedMb = running.reduce((sum, vm) => sum + vm.memoryUsedMb, 0);
  // VM 以外（Proxmox 本体・ZFS のキャッシュなど）。VM の数字とは測り方が違うので目安
  const hostMb = Math.max(0, node.memoryUsedMb - vmUsedMb);
  return (
    <details className="node-vms small">
      <summary className="muted">
        VM {vms.length} 台（動作中 {running.length}）
      </summary>
      <ul>
        {vms.map((vm) => (
          <li key={vm.vmid} className={vm.running ? undefined : 'faint'}>
            <span className="node-vm-name">
              {vm.name} <span className="faint">{KIND_LABEL[vm.kind]}</span>
            </span>
            <span>
              {vm.running
                ? `${formatGb(vm.memoryUsedMb)} / ${formatGb(vm.memoryMb)}・CPU ${percent(vm.cpu)}`
                : `停止中・割り当て ${formatGb(vm.memoryMb)}`}
            </span>
          </li>
        ))}
        {node.online && (
          <li>
            <span className="node-vm-name">
              VM 以外 <span className="faint">Proxmox 本体・キャッシュ（目安）</span>
            </span>
            <span>{formatGb(hostMb)}</span>
          </li>
        )}
      </ul>
    </details>
  );
}
